import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/Base/Popover";
import { Card, Employee, TerminologyOptions } from "@/types/v2";
import {
  getEmployeeName,
  getEmployeeInitials,
  getServiceAdvisorColor,
} from "@/lib/employees";
import { getUniqueTechnicianIdsFromCard } from "@/lib/jobState";
import { cn, getTextColor } from "@/lib/utils";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer";

export interface CardTechSALabelProps {
  cardData: Card;
  employees?: Employee[];
  /** Shop setting: false hides the whole badge row, advisor popover included. */
  showTechnician?: boolean;
  /** Shop setting: false limits the card to a single technician badge. */
  showAdditionalTechnicians?: boolean;
}

const MAX_VISIBLE_TECH_BADGES = 2;

interface TechBadgeProps {
  label: string;
  backgroundColor: string;
  children: React.ReactNode;
}

const TechBadge = ({ label, backgroundColor, children }: TechBadgeProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={cn(
          "z-5 cursor-pointer rounded-full p-0 text-accent hover:scale-105"
        )}
        onMouseDown={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div
          data-testid="tech-badge"
          className="flex h-fit min-h-[14px] w-fit min-w-[14px] items-center justify-center rounded-[4px] border border-white px-1 py-0.5 shadow-[inset_1px_1px_1px_#0000001A]"
          style={{ backgroundColor }}
        >
          <div
            className="text-[10px] font-bold leading-none"
            // getTextColor only parses 6-digit hex; the "#333" no-advisor
            // fallback yields undefined, which stays white.
            style={{ color: getTextColor(backgroundColor) ?? "white" }}
          >
            {label}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        onMouseDown={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        className="flex flex-col gap-2 p-2"
      >
        {children}
      </PopoverContent>
    </Popover>
  );
};

export const CardTechSALabel = ({
  cardData,
  employees = [],
  showTechnician = true,
  showAdditionalTechnicians = true,
}: CardTechSALabelProps) => {
  const { terminolize } = useTerminologyReplacer();
  const serviceAdvisorLabel = terminolize(
    "Service Advisor",
    TerminologyOptions.serviceAdvisor,
    "singular"
  );
  const technicianLabel = terminolize(
    "Technician",
    TerminologyOptions.technician,
    "singular"
  );
  const technicianPluralLabel = terminolize(
    "Technicians",
    TerminologyOptions.technician,
    "plural"
  );

  const backgroundColor = getServiceAdvisorColor(
    cardData?.serviceWriterId,
    employees || []
  );
  const serviceAdvisorName = cardData?.serviceWriterId
    ? getEmployeeName(cardData.serviceWriterId, employees || [])
    : "N/A";

  // getUniqueTechnicianIdsFromCard puts the RO's own technician first, so
  // keeping only the head honors "the RO's tech if assigned, else a job tech".
  const allTechnicianIds = getUniqueTechnicianIdsFromCard(cardData);
  const technicianIds = showAdditionalTechnicians
    ? allTechnicianIds
    : allTechnicianIds.slice(0, 1);
  // A card with no technicians at all still shows one (blank) badge, same
  // as before this change, so a card with no jobs/technicianId doesn't
  // lose its Service-Advisor-only popover slot.
  const displayIds: (string | null)[] =
    technicianIds.length > 0 ? technicianIds : [null];
  // Exactly MAX_VISIBLE_TECH_BADGES techs show one badge each as usual, but
  // beyond that we show only the first tech's badge plus a +N overflow
  // covering everyone else, rather than filling all visible slots first.
  const visibleCount =
    technicianIds.length > MAX_VISIBLE_TECH_BADGES
      ? 1
      : MAX_VISIBLE_TECH_BADGES;
  const visibleIds = displayIds.slice(0, visibleCount);
  const overflowIds = technicianIds.slice(visibleCount);

  if (!showTechnician) return null;

  return (
    <div className="flex flex-row items-center gap-0.5">
      {visibleIds.map((technicianId, index) => (
        <TechBadge
          key={technicianId ?? `empty-${index}`}
          label={getEmployeeInitials(technicianId, employees || [])}
          backgroundColor={backgroundColor}
        >
          <div>
            <p>
              <strong>{serviceAdvisorLabel}:</strong> {serviceAdvisorName}
            </p>
            <p>
              <strong>{technicianLabel}:</strong>{" "}
              {technicianId
                ? getEmployeeName(technicianId, employees || [])
                : "N/A"}
            </p>
          </div>
        </TechBadge>
      ))}
      {overflowIds.length > 0 && (
        <TechBadge
          label={`+${overflowIds.length}`}
          backgroundColor={backgroundColor}
        >
          <div>
            <p>
              <strong>{technicianPluralLabel}:</strong>
            </p>
            <ul className="list-disc pl-4">
              {overflowIds.map((id) => (
                <li key={id}>{getEmployeeName(id, employees || [])}</li>
              ))}
            </ul>
          </div>
        </TechBadge>
      )}
    </div>
  );
};
